import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import {
  Alert,
  Modal,
  CreditCardOutlineIcon,
  CalendarIcon,
} from '@clearcover/atlas';
import {
  GetPaymentsSummaryByTermNumberResponse,
  GetPolicyResponseTypeV2,
} from '@clearcover/core-app-types';

import { Column, HRule, Loading, Row, useToast } from 'Shared/components';
import { useResourceEffectiveGrants } from 'Shared/hooks';
import { useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { combineStrings } from 'Shared/utils';
import {
  manageAutopayPolicy,
  managePaymentInformationPolicy,
} from 'Admin/authPolicies';
import { PolicyContext } from 'Policies/utils';
import { AddDefaultPaymentMethodForm } from './AddDefaultPaymentMethodForm';
import { EditAutoPayScheduleForm } from './EditAutoPayScheduleForm';
import { AgentActionButton } from './AgentActionButton';
import styles from './styles.module.css';

export const PolicyManagement: FC<{
  policyId: string;
}> = ({ policyId }) => {
  const [
    {
      data: policy,
      loading: policyLoading,
      error: policyError,
      refetch: fetchPolicy,
    },
  ] = useNewApiV2<GetPolicyResponseTypeV2>(
    { url: `/policies/${policyId}` },
    false,
    false
  );

  const [draftList, setDraftList] = useState<
    GetPolicyResponseTypeV2['draftList']
  >([]);

  useEffect(() => {
    setDraftList(policy?.draftList ?? []);
  }, [policy?.draftList]);

  const [
    {
      loading: billingLoading,
      data: billingInfoResponse,
      refetch: fetchBillingData,
      error: billingError,
    },
  ] = useNewApiV2<GetPaymentsSummaryByTermNumberResponse>(
    {
      url: `/payments/${policyId}/summary/`,
    },
    false,
    false
  );

  const { resourceEffectiveGrants } = useResourceEffectiveGrants(policyId);
  const { toast } = useToast();
  const refetchHandler = useCallback(() => {
    fetchPolicy();
    fetchBillingData();
  }, [fetchPolicy, fetchBillingData]);

  const canEditPaymentInfo = useMemo(() => {
    return resourceEffectiveGrants
      ? managePaymentInformationPolicy(resourceEffectiveGrants)
      : false;
  }, [resourceEffectiveGrants]);

  const canManageAutoPay = useMemo(() => {
    return resourceEffectiveGrants
      ? manageAutopayPolicy(resourceEffectiveGrants)
      : false;
  }, [resourceEffectiveGrants]);

  const {
    paymentDueDate = '',
    autoPayEligibility = {
      eligible: false,
    },
    allowAutoPayDraftUpdate = false,
    autoPayDraftDay = 0,
  } = billingInfoResponse?.billingInfo || {};

  const eligibleToUpdateAutoPay =
    canManageAutoPay && allowAutoPayDraftUpdate && autoPayEligibility.eligible;

  // as we add items here they will have different permissions, the container should render if any of them are true
  const showPolicyManagement = [
    canEditPaymentInfo,
    eligibleToUpdateAutoPay,
  ].some((v) => v);

  const onSuccess = (message: string) => {
    toast({
      message,
      type: 'success',
      withSecondaryNav: false,
      position: 'top',
    });
  };

  if (policyLoading || billingLoading) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  if (policyError || billingError) {
    return (
      <Alert
        className={styles.policyError}
        message="There was an error loading Additional Agent Features"
      />
    );
  }

  return policy ? (
    <PolicyContext.Provider
      value={{
        setDriverOptions: () => {},
        driverOptions: null,
        details: {
          region: policy.policyDetails.policyRegion,
          effectiveDate: policy.policyDetails.effectivePeriod.startsAt,
          timeZone: policy.policyDetails.policyTimeZone,
          filingIdentifier: policy.policyDetails.filingIdentifier,
          configDiscoveryParams: policy.policyDetails.configDiscoveryParams,
          isPolicyDraft: false,
          draftType: undefined,
          isRenewalDraft: false,
          termId: policy.policyDetails.termInfo.termId,
          mailingState: policy.policyDetails.addresses.mailing.state,
          showDriverCfrs: policy.policyDetails.showDriverCfrs,
          askForUserReportedLicenseStatus:
            policy.policyDetails.askForUserReportedLicenseStatus ?? false,
          requiredAccessStatements: policy.requiredAccessStatements,
          showMilitarySection: policy.policyDetails.showMilitarySection ?? true,
          showStudentSection: policy.policyDetails.showStudentSection ?? true,
          showEducation: policy.policyDetails.showEducation ?? true,
          showOccupation: policy.policyDetails.showOccupation ?? true,
          showRegisteredToApplicantOrSpouse:
            policy.policyDetails.showRegisteredToApplicantOrSpouse ?? true,
          showExcludedDriverGender:
            policy.policyDetails.showExcludedDriverGender ?? false,
          showExcludedDriverMaritalStatus:
            policy.policyDetails.showExcludedDriverMaritalStatus ?? false,
          showPricingData: policy.policyDetails.showPricingData ?? true,
        },
        person: {
          name: combineStrings([
            policy.policyHolder.name.firstName,
            policy.policyHolder.name.lastName,
          ]),
        },
        flags: {
          renewalQuotesEnabled: false, // unused in this context
          isFailingUnderwriting: false, // unused in this context
          mandatedDownPay: policy.mandatedDownPay,
        },
        canEndorseDuringRegionalEvents: false, // unused in this context
        isEndorseablePolicy: false, // unused in this context
        systemOfRecord: policy.systemOfRecord || 'clearcover',
        resourceEffectiveGrants,
        refetchHandler,
        loading: policyLoading || billingLoading,
        productSegment: 'standardAuto',
        draftList,
        removeDraft: (draftId) =>
          setDraftList(draftList.filter((draft) => draft.id !== draftId)),
      }}
    >
      {policy.systemOfRecord === 'clearcover' && showPolicyManagement ? (
        <div className={styles.container}>
          <HRule size="lg" className={styles.hrule} />
          <h3 className={styles.header}>Additional Agent Features</h3>
          <Row className={styles.features}>
            {eligibleToUpdateAutoPay && (
              <Column className={styles.feature}>
                <Modal
                  data-testid="manage-autopay-modal"
                  variant="xl"
                  toggle={(open) => (
                    <AgentActionButton
                      icon={CalendarIcon}
                      label="Update AutoPay Date"
                      onClick={open}
                    />
                  )}
                >
                  {(close) => (
                    <EditAutoPayScheduleForm
                      policyId={policyId}
                      close={close}
                      onSuccess={onSuccess}
                      currentDueDay={DateTime.fromISO(paymentDueDate).day}
                      currentAutoPayDraftDay={
                        autoPayDraftDay
                          ? autoPayDraftDay
                          : DateTime.fromISO(paymentDueDate).day
                      }
                    />
                  )}
                </Modal>
              </Column>
            )}
            {canEditPaymentInfo && (
              <Column className={styles.feature}>
                <Modal
                  data-testid="update-payment-method-modal"
                  variant="tall"
                  toggle={(open) => (
                    <AgentActionButton
                      icon={CreditCardOutlineIcon}
                      label="Update Payment Method"
                      onClick={open}
                    />
                  )}
                >
                  {(close) => (
                    <AddDefaultPaymentMethodForm
                      policyId={policyId}
                      close={close}
                      onSuccess={onSuccess}
                      billingInfo={billingInfoResponse?.billingInfo}
                    />
                  )}
                </Modal>
              </Column>
            )}
          </Row>
        </div>
      ) : null}
    </PolicyContext.Provider>
  ) : null;
};
